import React, { useState, useMemo, useCallback } from 'react';
import { Button, Checkbox, ListItemText, TextField, InputAdornment, Collapse } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import styled from 'styled-components';
import { colors } from 'shared/themes';
import { ChainDispensary, ChainSelectProps, DispensariesByState } from './chain-dispensary-select.types';
import { useCheckboxStyles, useListItemStyles } from './chain-dispensary-select.styles';

/**
 * Filters dispensaries by state based on search term.
 * Returns a new Map with only dispensaries that match the search.
 */
function filterDispensariesByState(dispensariesByState: DispensariesByState, searchTerm: string): DispensariesByState {
  if (!searchTerm.trim()) {
    return dispensariesByState;
  }

  const lowerSearch = searchTerm.toLowerCase();
  const filteredMap = new Map<string, ChainDispensary[]>();

  dispensariesByState.forEach((dispensaries, state) => {
    const filtered = dispensaries.filter(
      (d) => d.name.toLowerCase().includes(lowerSearch) || d.address.toLowerCase().includes(lowerSearch)
    );
    if (filtered.length > 0) {
      filteredMap.set(state, filtered);
    }
  });

  return filteredMap;
}

/**
 * Filters a flat list of dispensaries based on search term.
 */
function filterDispensaries(dispensaries: ChainDispensary[], searchTerm: string): ChainDispensary[] {
  if (!searchTerm.trim()) {
    return dispensaries;
  }

  const lowerSearch = searchTerm.toLowerCase();
  return dispensaries.filter(
    (d) => d.name.toLowerCase().includes(lowerSearch) || d.address.toLowerCase().includes(lowerSearch)
  );
}

type InlineSelectProps = Omit<ChainSelectProps, 'createStickyHeaderLabel'> & {
  /** Maximum height of the scrollable list area. Defaults to 400px */
  maxHeight?: number;
  /** Whether to show the header with selected count and select all button */
  showHeader?: boolean;
};

/**
 * InlineChainDispensarySelect displays dispensaries in a scrollable inline list
 * instead of a dropdown. Ideal for modals where users need to select from
 * hundreds of locations.
 *
 * Features:
 * - Inline search with instant filtering
 * - State-grouped dispensaries with collapsible sections
 * - Select all / deselect all functionality
 * - Individual state selection
 * - Cmd/Ctrl+click to expand/collapse all states
 */
export function InlineChainDispensarySelect({
  handleChange,
  hasAllSelected,
  hasStaleChainLocations,
  options,
  selectedChainRetailerIds,
  selectedDispensaryIds,
  toggleSelectedAll,
  dispensariesByState,
  isStateFullySelected,
  isStatePartiallySelected,
  toggleStateSelection,
  maxHeight = 400,
  showHeader = true,
}: InlineSelectProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedStates, setCollapsedStates] = useState<Set<string>>(new Set());

  const checkboxClasses = useCheckboxStyles();
  const listItemClasses = useListItemStyles();
  const locations = hasStaleChainLocations ? selectedChainRetailerIds : selectedDispensaryIds;
  const selectedAmount = locations.length;

  /**
   * Toggles the collapsed state of a state group.
   * If toggleAll is true, expands or collapses all states based on the clicked state's current status.
   */
  const toggleCollapse = useCallback(
    (state: string, toggleAll = false) => {
      setCollapsedStates((prev) => {
        const isCurrentlyCollapsed = prev.has(state);

        if (toggleAll) {
          if (isCurrentlyCollapsed) {
            return new Set();
          }
          return new Set(dispensariesByState.keys());
        }

        const next = new Set(prev);
        if (isCurrentlyCollapsed) {
          next.delete(state);
        } else {
          next.add(state);
        }
        return next;
      });
    },
    [dispensariesByState]
  );

  /**
   * Toggles the selection of an individual dispensary.
   */
  const toggleDispensarySelection = useCallback(
    (dispensaryId: string) => {
      const isSelected = locations.includes(dispensaryId);
      const newLocations = isSelected ? locations.filter((id) => id !== dispensaryId) : [...locations, dispensaryId];

      const syntheticEvent = {
        target: { value: newLocations },
      } as React.ChangeEvent<{ value: unknown }>;

      handleChange(syntheticEvent);
    },
    [locations, handleChange]
  );

  // Filter dispensaries based on search term
  const filteredDispensariesByState = useMemo(() => filterDispensariesByState(dispensariesByState, searchTerm), [
    dispensariesByState,
    searchTerm,
  ]);

  const filteredOptions = useMemo(() => filterDispensaries(options, searchTerm), [options, searchTerm]);

  // Check if we have multiple states to show grouped view
  const hasMultipleStates = dispensariesByState.size > 1;

  const hasNoResults =
    (hasMultipleStates && filteredDispensariesByState.size === 0) ||
    (!hasMultipleStates && filteredOptions.length === 0);

  // Count total locations for display
  const totalLocations = options.length;

  return (
    <Container>
      {showHeader && (
        <Header>
          <HeaderLeft>
            <SelectedCount>
              {selectedAmount} of {totalLocations} selected
            </SelectedCount>
          </HeaderLeft>
          <HeaderRight>
            <SelectAllButton
              onClick={(e: React.MouseEvent): void => toggleSelectedAll(e as React.MouseEvent<HTMLAnchorElement>)}
            >
              {hasAllSelected ? 'Deselect All' : 'Select All'}
            </SelectAllButton>
          </HeaderRight>
        </Header>
      )}

      <SearchContainer>
        <StyledTextField
          placeholder='Search locations by name or address...'
          variant='outlined'
          size='small'
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon style={{ color: '#9CA3AF', fontSize: '20px' }} />
              </InputAdornment>
            ),
          }}
        />
      </SearchContainer>

      <ListContainer maxHeight={maxHeight}>
        {hasNoResults && <NoResultsText>No locations found matching "{searchTerm}"</NoResultsText>}

        {!hasNoResults && hasMultipleStates && (
          <>
            {Array.from(filteredDispensariesByState.entries()).map(([state, dispensaries]) => {
              const isFullySelected = isStateFullySelected(state);
              const isPartiallySelected = isStatePartiallySelected(state);
              const isCollapsed = collapsedStates.has(state);

              return (
                <StateGroup key={state}>
                  <StateHeader>
                    <CollapseToggle
                      onClick={(e) => {
                        toggleCollapse(state, e.metaKey || e.ctrlKey);
                      }}
                      aria-label={isCollapsed ? `Expand ${state}` : `Collapse ${state}`}
                    >
                      {isCollapsed ? <ChevronRightIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
                    </CollapseToggle>
                    <Checkbox
                      classes={checkboxClasses}
                      checked={isFullySelected}
                      indeterminate={isPartiallySelected}
                      onClick={() => toggleStateSelection(state)}
                    />
                    <StateHeaderText
                      onClick={(e) => {
                        toggleCollapse(state, e.metaKey || e.ctrlKey);
                      }}
                    >
                      {state} ({dispensaries.length})
                    </StateHeaderText>
                  </StateHeader>

                  <Collapse in={!isCollapsed} timeout='auto' unmountOnExit>
                    <DispensaryList>
                      {dispensaries.map(({ id, name, address }: ChainDispensary) => (
                        <DispensaryItem
                          key={id}
                          isSelected={locations.includes(id)}
                          onClick={() => toggleDispensarySelection(id)}
                        >
                          <Checkbox classes={checkboxClasses} checked={locations.includes(id)} />
                          <ListItemText classes={listItemClasses} primary={name} secondary={address} />
                        </DispensaryItem>
                      ))}
                    </DispensaryList>
                  </Collapse>
                </StateGroup>
              );
            })}
          </>
        )}

        {!hasNoResults && !hasMultipleStates && (
          <DispensaryList>
            {filteredOptions.map(({ id, name, address }: ChainDispensary) => (
              <DispensaryItem
                key={id}
                isSelected={locations.includes(id)}
                onClick={() => toggleDispensarySelection(id)}
              >
                <Checkbox classes={checkboxClasses} checked={locations.includes(id)} />
                <ListItemText classes={listItemClasses} primary={name} secondary={address} />
              </DispensaryItem>
            ))}
          </DispensaryList>
        )}
      </ListContainer>
    </Container>
  );
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  min-width: 500px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const SelectedCount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.v2TextColor1};
`;

const SelectAllButton = styled(Button)`
  && {
    text-transform: none;
    color: ${colors.primaryBlue};
    font-size: 14px;
    padding: 4px 8px;
    min-width: auto;

    &:hover {
      background-color: ${colors.blue[90]};
    }
  }
`;

const SearchContainer = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
`;

const StyledTextField = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: 6px;
      font-size: 14px;

      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: #b6b6b6;
      }

      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${colors.primaryBlue};
        border-width: 1px;
      }
    }

    .MuiOutlinedInput-input {
      padding: 10px 12px;
    }
  }
`;

const ListContainer = styled.div<{ maxHeight: number }>`
  max-height: ${({ maxHeight }) => maxHeight}px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
`;

const StateGroup = styled.div`
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const StateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 12px;
  background: #f9fafb;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f3f4f6;
  }
`;

const CollapseToggle = styled.button`
  background: none;
  border: none;
  padding: 4px;
  margin-right: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #6b7280;
  border-radius: 4px;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const StateHeaderText = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${colors.v2TextColor1};
  flex: 1;
  cursor: pointer;
  text-align: left;
`;

const DispensaryList = styled.div`
  padding: 4px 0;
`;

const DispensaryItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: flex-start;
  padding: 8px 16px 8px 36px;
  cursor: pointer;
  border-radius: 4px;
  margin: 2px 8px;
  background: ${({ isSelected }) => (isSelected ? colors.blue[90] : 'transparent')};

  &:hover {
    background: ${({ isSelected }) => (isSelected ? colors.blue[90] : '#f6f6f6')};
  }

  /* Ensure text is left-aligned */
  .MuiListItemText-root {
    text-align: left;
    margin: 0;
  }
`;

const NoResultsText = styled.div`
  padding: 32px 16px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
`;
